import { array } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { FeatureFlag } from '@mews-distributor/common';
import { BookingEngine, ConfigurationResult } from '@mews-distributor/data-access';
import { primitiveTypeUniq } from '@mews/fp';
import { createAction } from '@mews/framework';
import { DEFAULT_LANGUAGE_CODE } from '../../constants';
import { ApplicationState, DistributorThunkAction } from '../../types';
import { LANGUAGE_SET } from '../app/actionTypes';
import { getClientApiLanguageCode, getLanguageCodeFromQuery } from '../app/selectors';
import { getLanguages, getValidLanguageCode, hasLanguageCode } from '../globalization/selectors';
import { getField } from './processConfiguration';

/**
 * Returns language code only when all the enterprises have the same language code.
 *
 * For other scenarios, we can't reasonably decide which one is right.
 */
const getEnterpriseLanguageCode = (configurationResult: ConfigurationResult) => {
    const languageCodes = pipe(
        configurationResult.enterprises,
        array.map((enterprise) => enterprise.defaultLanguageCode),
        primitiveTypeUniq
    );

    const firstLanguageCode = languageCodes[0];

    return languageCodes.length === 1 ? firstLanguageCode : null;
};

/** Sets configured currency code only when it makes sense. */
export const setConfiguredLanguageCode =
    ({
        bookingEngineConfiguration,
        configurationResult,
    }: {
        bookingEngineConfiguration: BookingEngine;
        configurationResult: ConfigurationResult;
    }): DistributorThunkAction =>
    (dispatch, getState, { featureFlagService }) => {
        const state = getState();
        const isUserCurrencyDetectionEnabled = featureFlagService.hasFlag(FeatureFlag.userCurrencyDetectionEnabled);

        const languageCodeFromClientApi = getClientApiLanguageCode(state);
        const languageCodeFromQuery = getLanguageCodeFromQuery(state);
        const validLanguageCodeFromQuery =
            languageCodeFromQuery != null ? getValidLanguageCode(state, { languageCode: languageCodeFromQuery }) : null;
        const languageCodeFromApiConfiguration = getValidLanguageCode(state, {
            languageCode: getField(bookingEngineConfiguration, 'languageCode'),
        });

        // Enable language detection from browser locale only when user currency detection is enabled.
        const languageCodeFromBrowserLocale = isUserCurrencyDetectionEnabled
            ? getBrowserLocaleLanguageCode(state)
            : null;

        const enterpriseLanguageCode = getEnterpriseLanguageCode(configurationResult);

        const languageCode =
            languageCodeFromClientApi ??
            validLanguageCodeFromQuery ??
            languageCodeFromBrowserLocale ??
            languageCodeFromApiConfiguration ??
            enterpriseLanguageCode;

        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        if (languageCode) {
            dispatch(setLanguage(languageCode));
        }
    };

export const setLanguage =
    (languageCode: string, { isClientApiCall = false } = {}): DistributorThunkAction =>
    (dispatch, _getState, { gainsightService, setHtmlLanguage }) => {
        setHtmlLanguage(languageCode);
        gainsightService.setGlobalContext({ languageDisplayed: languageCode });
        return dispatch(createAction(LANGUAGE_SET, { languageCode, isClientApiCall }));
    };

export const checkLanguageConfiguration =
    (languageCode?: string, { isClientApiCall = false } = {}): DistributorThunkAction =>
    (dispatch, getState) => {
        if (languageCode == null || !hasLanguageCode(getState(), { languageCode })) {
            dispatch(setLanguage(DEFAULT_LANGUAGE_CODE));
        } else {
            dispatch(setLanguage(languageCode, { isClientApiCall }));
        }
    };

const getBrowserLocaleLanguageCode = (state: ApplicationState) => {
    const languageCodeFromBrowserLocale = navigator.language;
    const supportedLanguages = getLanguages(state);
    const languageCode =
        supportedLanguages[languageCodeFromBrowserLocale] != null ? languageCodeFromBrowserLocale : null;

    return languageCode;
};
